import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { distinctUntilChanged, Observable, of, ReplaySubject, tap } from 'rxjs';
import { UserService } from '../user/user.service';
import { FuseNavigationItem } from '../../../@fuse/components/navigation/navigation.types';
import { Navigation, RecentService } from './navigation.types';
import { UserNodePrivileges } from '../../modules/admin/projects/projects.types';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {

    compactNavigation: FuseNavigationItem[] = [];
    defaultNavigation: FuseNavigationItem[] = [];
    futuristicNavigation: FuseNavigationItem[] = [];
    horizontalNavigation: FuseNavigationItem[] = [];

    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
    private router = inject(Router);

    private readonly maxNumberOfRecentServices = 5;

    /**
     * Constructor
     */
    constructor(
        private translocoService: TranslocoService,
        private userService: UserService
    ) {
        this.userService.role$.pipe(distinctUntilChanged()).subscribe((role) => {
            if (role !== undefined) {
                this.generateNavigation(role);
                this.generateRecentServices(role);
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return of<Navigation>({
            compact: cloneDeep(this.compactNavigation),
            default: cloneDeep(this.defaultNavigation),
            futuristic: cloneDeep(this.futuristicNavigation),
            horizontal: cloneDeep(this.horizontalNavigation),
        }).pipe(
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    addRecentService(id: string, name: string, link: string) {
        const recentServices: RecentService[] = JSON.parse(localStorage.getItem('recent-services')) ?? [];
        const service: RecentService = {
            id: id,
            title: name,
            link: link
        };
        const index = recentServices.findIndex(s => s.id === id);
        if (index < 0) {
            if (recentServices?.length === this.maxNumberOfRecentServices) {
                recentServices.pop();
            }
            recentServices.unshift(service);
            localStorage.setItem('recent-services', JSON.stringify(recentServices));
            this.addChildrenToRecentServices(recentServices);
        }
    }

    removeRecentService(id: string) {
        const recentServices: RecentService[] = JSON.parse(localStorage.getItem('recent-services')) ?? [];
        const index = recentServices.findIndex(s => s.id === id);
        if (index > -1) {
            recentServices.splice(index, 1);
        }
        localStorage.setItem('recent-services', JSON.stringify(recentServices));
        this.addChildrenToRecentServices(recentServices);
    }

    private generateRecentServices(role: UserNodePrivileges) {
        this.compactNavigation = this.compactNavigation.filter(n => n.id !== 'recent-services');
        this.defaultNavigation = this.defaultNavigation.filter(n => n.id !== 'recent-services');
        this.futuristicNavigation = this.futuristicNavigation.filter(n => n.id !== 'recent-services');
        this.horizontalNavigation = this.horizontalNavigation.filter(n => n.id !== 'recent-services');

        let recentServices: RecentService[] = JSON.parse(localStorage.getItem('recent-services')) ?? [];
        if (recentServices.some(s => !s.link.includes('workshifts') && !s.link.includes('tasks') && !s.link.includes('housekeeping') && !s.link.includes('center') && !s.link.includes('generic'))) {
            recentServices = recentServices.filter(s => s.link.includes('workshifts') || s.link.includes('tasks') || s.link.includes('housekeeping') || s.link.includes('center') || s.link.includes('generic'));
            localStorage.setItem('recent-services', JSON.stringify(recentServices));
        }
        const commonRecentServices: FuseNavigationItem = {
            id: 'recent-services',
            title: this.translocoService.translate('recentServices'),
            type: 'collapsable',
            icon: 'mat_solid:history',
            children: [
                {
                    id: 'noservices',
                    title: this.translocoService.translate('noRecentServices'),
                    type: 'basic',
                    disabled: true,
                }
            ]
        };
        if (this.compactNavigation.findIndex(n => n.id === 'recent-services') === -1) {
            this.compactNavigation.push({
                id: 'recent-services',
                title: this.translocoService.translate('recentServices'),
                type: 'aside',
                icon: 'mat_solid:history',
                children: [
                    {
                        id: 'noservices',
                        title: this.translocoService.translate('noRecentServices'),
                        type: 'basic',
                        disabled: true,
                    }
                ]
            });
        }
        if (this.defaultNavigation.findIndex(n => n.id === 'recent-services') === -1) {
            this.defaultNavigation.push(commonRecentServices);
        }
        if (this.futuristicNavigation.findIndex(n => n.id === 'recent-services') === -1) {
            this.futuristicNavigation.push(commonRecentServices);
        }
        if (this.horizontalNavigation.findIndex(n => n.id === 'recent-services') === -1) {
            this.horizontalNavigation.push(commonRecentServices);
        }
        if (recentServices?.length > 0) {
           this.addChildrenToRecentServices(recentServices);
           if (recentServices.length === 1 && !role) {
                this.router.navigate([recentServices[0].link]);
           }
        }
    }

    private addChildrenToRecentServices(recentServices: RecentService[]) {
        const children: FuseNavigationItem[] = [];
        for (const service of recentServices) {
            children.push({
                id: service.id,
                title: service.title,
                type: 'basic',
                icon: 'mat_outline:list_alt',
                link: service.link
            });
        }
        this.compactNavigation.find(n => n.id === 'recent-services').children = children;
        this.defaultNavigation.find(n => n.id === 'recent-services').children = children;
        this.futuristicNavigation.find(n => n.id === 'recent-services').children = children;
        this.horizontalNavigation.find(n => n.id === 'recent-services').children = children;

        this._navigation.next({
            compact: cloneDeep(this.compactNavigation),
            default: cloneDeep(this.defaultNavigation),
            futuristic: cloneDeep(this.futuristicNavigation),
            horizontal: cloneDeep(this.horizontalNavigation),
        });
    }

    private generateNavigation(role: UserNodePrivileges) {
        this.compactNavigation = [];
        this.defaultNavigation = [];
        this.futuristicNavigation = [];
        this.horizontalNavigation = [];
        if (role?.adminMeAndChildren) {
            this.compactNavigation.push({
                id: 'admin',
                title: this.translocoService.translate('administration'),
                tooltip: this.translocoService.translate('administration'),
                type: 'aside',
                icon: 'mat_outline:admin_panel_settings',
                children: [
                    {
                        id: 'grupoconstant',
                        title: 'GrupoConstant',
                        type: 'basic',
                        icon: 'mat_outline:workspaces',
                        link: '/ou'
                    }
                ]
            });
            const defaultItem: FuseNavigationItem = {
                id: 'admin',
                title: this.translocoService.translate('administration'),
                type: 'collapsable',
                icon: 'mat_outline:admin_panel_settings',
                children: [
                    {
                        id: 'grupoconstant',
                        title: 'GrupoConstant',
                        type: 'basic',
                        icon: 'mat_outline:workspaces',
                        link: '/ou'
                    }
                ]
            };
            this.defaultNavigation.push(defaultItem);
            this.futuristicNavigation.push(defaultItem);
            this.horizontalNavigation.push(defaultItem);
        }
        this.compactNavigation.push({
            id: 'projects',
            title: this.translocoService.translate('projects'),
            tooltip: this.translocoService.translate('projects'),
            type: 'basic',
            icon: 'heroicons_outline:briefcase',
            link: '/projects'
        });
        const commonProjectItem: FuseNavigationItem = {
            id: 'projects',
            title: this.translocoService.translate('projects'),
            type: 'basic',
            icon: 'heroicons_outline:briefcase',
            link: '/projects'
        };
        this.defaultNavigation.push(commonProjectItem);
        this.futuristicNavigation.push(commonProjectItem);
        this.horizontalNavigation.push(commonProjectItem);

        if (role?.adminMobileUser) {
            this.compactNavigation.push({
                id: 'connected-device',
                title: this.translocoService.translate('connectedDevices'),
                tooltip: this.translocoService.translate('connectedDevices'),
                type: 'basic',
                icon: 'heroicons_outline:device-mobile',
                link: '/connected-device'
            });
            this.compactNavigation.push({
                id: 'mission-users',
                title: this.translocoService.translate('missionUsers'),
                tooltip: this.translocoService.translate('missionUsers'),
                type: 'basic',
                icon: 'heroicons_solid:users',
                link: '/mission-users'
            });
            const commonMobileUserItems: FuseNavigationItem[] = [{
                id: 'connected-device',
                title: this.translocoService.translate('connectedDevices'),
                type: 'basic',
                icon: 'heroicons_outline:device-mobile',
                link: '/connected-device'
            }, {
                id: 'mission-users',
                title: this.translocoService.translate('missionUsers'),
                type: 'basic',
                icon: 'heroicons_solid:users',
                link: '/mission-users'
            }];
            this.defaultNavigation.push(...commonMobileUserItems);
            this.futuristicNavigation.push(...commonMobileUserItems);
            this.horizontalNavigation.push(...commonMobileUserItems);
        }

        this._navigation.next({
            compact: cloneDeep(this.compactNavigation),
            default: cloneDeep(this.defaultNavigation),
            futuristic: cloneDeep(this.futuristicNavigation),
            horizontal: cloneDeep(this.horizontalNavigation),
        });
    }
}
